import React, { useState } from 'react';
import { FiDollarSign, FiCalendar, FiTrendingUp, FiTrendingDown, FiInfo, FiLoader } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/client';

interface ProjectionResult {
  master: { _id: string; name: string; profileImage?: string };
  input: { investmentAmount: number; days: number };
  historical: {
    totalTrades: number;
    winTrades: number;
    lossTrades: number;
    winRate: number;
    totalPnl: number;
    accountBalance: number;
    roiPercent: number;
    periodDays: number;
    dailyRoiPercent: number;
    firstTradeAt: string | null;
  };
  projection: {
    roiPercent: number;
    estimatedProfit: number;
    estimatedValue: number;
    isProfit: boolean;
  };
  disclaimer: string;
}

const DAY_PRESETS = [7, 30, 90, 180, 365];

interface ProfitCalculatorPanelProps {
  masterId: string;
  masterName?: string;
}

const ProfitCalculatorPanel: React.FC<ProfitCalculatorPanelProps> = ({ masterId, masterName }) => {
  const [investment, setInvestment] = useState<string>('1000');
  const [days, setDays] = useState<number>(30);
  const [submittedKey, setSubmittedKey] = useState<{ investment: number; days: number } | null>(null);

  const { data, isFetching, error, refetch } = useQuery<{ success: boolean } & ProjectionResult>({
    queryKey: ['profitCalculator', masterId, submittedKey?.investment, submittedKey?.days],
    queryFn: async () => {
      const response = await api.get(`/master-details/${masterId}/profit-calculator`, {
        params: { investment: submittedKey?.investment, days: submittedKey?.days },
      });
      return response.data;
    },
    enabled: !!submittedKey,
  });

  const handleCalculate = () => {
    const investmentAmount = Math.max(0, Number(investment) || 0);
    const safeDays = Math.max(1, Number(days) || 1);
    setSubmittedKey({ investment: investmentAmount, days: safeDays });
    if (submittedKey) refetch();
  };

  const result = data;

  return (
    <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm space-y-6 min-w-0">
      <div className="min-w-0">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 break-anywhere">
          <FiTrendingUp className="h-5 w-5 text-primary-600 shrink-0" />
          Profit Calculator{masterName ? ` — ${masterName}` : ''}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Estimate what your investment could return, projected from this master's historical performance.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Investment amount</label>
          <div className="relative">
            <FiDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="number"
              min={0}
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="1000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time horizon (days)</label>
          <div className="relative">
            <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(Number(e.target.value) || 1)}
              className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-500"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {DAY_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setDays(preset)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-colors ${
                  days === preset
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {preset}d
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleCalculate}
          disabled={isFetching}
          className="btn-primary py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {isFetching ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiTrendingUp className="h-4 w-4" />}
          {isFetching ? 'Calculating...' : 'Calculate'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {(error as any)?.response?.data?.error || 'Failed to calculate projection. Please try again.'}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div
            className={`rounded-lg p-4 sm:p-5 border-2 ${
              result.projection.isProfit
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <p className="text-sm text-gray-600">
                  Investing <strong>${result.input.investmentAmount.toLocaleString()}</strong> for{' '}
                  <strong>{result.input.days}</strong> day{result.input.days === 1 ? '' : 's'}, based on{' '}
                  {result.master.name}'s historical performance, projects to:
                </p>
                <p
                  className={`text-2xl sm:text-3xl font-bold mt-1 flex flex-wrap items-center gap-2 break-anywhere ${
                    result.projection.isProfit ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {result.projection.isProfit ? (
                    <FiTrendingUp className="h-6 w-6" />
                  ) : (
                    <FiTrendingDown className="h-6 w-6" />
                  )}
                  {result.projection.isProfit ? '+' : ''}
                  ${result.projection.estimatedProfit.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  <span className="text-base font-semibold text-gray-500">
                    ({result.projection.roiPercent >= 0 ? '+' : ''}
                    {result.projection.roiPercent}%)
                  </span>
                </p>
              </div>
              <div className="text-left sm:text-right min-w-0">
                <p className="text-sm text-gray-500">Estimated account value</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 break-anywhere">
                  ${result.projection.estimatedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">All-time ROI</p>
              <p className="font-bold text-gray-900">{result.historical.roiPercent}%</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Win Rate</p>
              <p className="font-bold text-gray-900">{result.historical.winRate}%</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Trades</p>
              <p className="font-bold text-gray-900">{result.historical.totalTrades}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Track Record</p>
              <p className="font-bold text-gray-900">{result.historical.periodDays}d</p>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
            <FiInfo className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>{result.disclaimer}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitCalculatorPanel;
